import * as React from 'react';
import { Button, DropdownProps, Form, Header, Modal, Segment } from 'semantic-ui-react';

import { Argumentos, clienteApi } from 'src/api';
import { BloqueHorario } from 'src/api/protocolo/horarios';
import { ModalEnfocable } from 'src/componentes/semantic-ui-extended';
import { Contexto } from 'src/contexto';
import { salas } from '../salas';

type Dias = 1 | 2 | 3 | 4 | 5;
interface TipoCurso {
    id: number;
    nombre: string;
    bloqueHorario: {
        horaInicio: string;
        horaTermino: string;
        tipo: 'normal' | 'vespertino';
    };
    sala: string;
    dia: number;
    colorDeSugerencia: string;
    inscritos: number;
    capacidad: number;
}

interface Props {
    cargando: boolean;
    cursos: ReadonlyArray<TipoCurso>;
    creandoNuevoCurso(): void;
    creacionTerminada(): Promise<void>;
}

const bloquesAnualIntensivo = [
    '08:30-10:00',
    '09:30-11:00',
    '10:15-11:45',
    '10:30-12:00',
    '11:15-12:45',
    '12:15-13:45',
    '15:00-16:30',
    '16:30-18:00',
    '16:45-18:15',
    '17:00-18:30',
    '18:30-20:00',
    '18:45-20:15',
    '19:00-20:10-v',
    '20:20-21:30-v',
] as const;

const bloquesIntensivoVespertino = [] as const;

const bloquesElectivo = [] as const;

function mapBloqueHorario<T extends string>(id: T) {
    const [inicio, termino, vesp] = id.split('-');
    const nombre = `${inicio} - ${termino}`;
    const vespertino = vesp === 'v';

    return { id, nombre, vespertino };
}

const bloquesHorariosAnualEIntensivo = bloquesAnualIntensivo.map(mapBloqueHorario);
const bloquesHorariosIntensivoVespertino = bloquesIntensivoVespertino.map(mapBloqueHorario);
const bloquesHorariosElectivo = bloquesElectivo.map(mapBloqueHorario);

const diaATexto: Record<number, string> = {
    1: 'lunes',
    2: 'martes',
    3: 'miércoles',
    4: 'jueves',
    5: 'viernes',
    6: 'sábado',
};

interface State {
    bloqueElegido: string;
    diaElegido: Dias | 0;
    bloqueHorarioElegido: string;
    planElegido: 'anual' | 'intensivo' | 'intensivo-vespertino' | 'electivo';
    salaElegida: string;
    sugerenciaElegida: number;
    modalNuevaSugerenciaAbierto: boolean;
    colorElegido: string;
    mostrandoCrearCurso: boolean;
}

const stateInicial: State = {
    bloqueElegido: '',
    diaElegido: 0,
    bloqueHorarioElegido: '',
    planElegido: 'anual',
    salaElegida: '1-3',
    sugerenciaElegida: 0,
    modalNuevaSugerenciaAbierto: false,
    colorElegido: '#ffffff',
    mostrandoCrearCurso: false,
};
class CrearNuevoCurso extends React.Component<Props, State> {
    public static readonly contextType = Contexto;
    public readonly context!: React.ContextType<typeof Contexto>;

    public state: State = stateInicial;

    private colorPicker: HTMLInputElement | null = null;

    private readonly planesOption = [
        { key: 'anual', value: 'anual', text: 'Anual' },
        { key: 'intensivo', value: 'intensivo', text: 'Intensivo' },
        {
            key: 'intensivo-vespertino',
            value: 'intensivo-vespertino',
            text: 'Intensivo vespertino',
        },
        { key: 'electivo', value: 'electivo', text: 'Electivo' },
    ];

    private readonly bloquesCienciasOptions = [
        { value: 'BIO', text: 'Biología' },
        { value: 'FIS', text: 'Física' },
        { value: 'QUI', text: 'Química' },
    ];

    private readonly bloquesOptions = [
        { value: 'MAT1', text: 'Matemática 1' },
        { value: 'MAT2', text: 'Matemática 2' },
        { value: 'LEN1', text: 'Lenguaje 1' },
        { value: 'LEN2', text: 'Lenguaje 2' },
        { value: 'HIS1', text: 'Historia 1' },
        { value: 'HIS2', text: 'Historia 2' },
        ...this.bloquesCienciasOptions,
    ];

    private readonly diasOptions = [
        { value: 1, text: 'Lunes' },
        { value: 2, text: 'Martes' },
        { value: 3, text: 'Miércoles' },
        { value: 4, text: 'Jueves' },
        { value: 5, text: 'Viernes' },
    ];

    private readonly salasOptions = salas.map(sala => ({
        value: sala.id,
        text: sala.nombre,
    }));

    public render() {
        if (!this.state.mostrandoCrearCurso) {
            return (
                <Button
                    primary
                    icon='add'
                    labelPosition='left'
                    content='Crear un nuevo curso'
                    onClick={() => {
                        this.setState({ mostrandoCrearCurso: true });
                    }}
                />
            );
        }
        const creacionEsValida =
            this.props.cargando === false &&
            this.state.bloqueElegido !== '' &&
            this.state.diaElegido !== 0 &&
            this.state.bloqueHorarioElegido !== '' &&
            this.state.salaElegida !== '' &&
            this.state.sugerenciaElegida !== 0;

        return (
            <Segment>
                <Header as='h3'>Crear un nuevo curso</Header>
                <Form>
                    <Form.Group>
                        <Form.Select
                            label='Plan'
                            options={this.planesOption}
                            name='planElegido'
                            value={this.state.planElegido}
                            onChange={this.manejarCambioCreacion}
                            width='2'
                            fluid
                        />
                        <Form.Select
                            label='Bloque/sesión (asignatura)'
                            options={
                                this.state.planElegido === 'electivo'
                                    ? this.bloquesCienciasOptions
                                    : this.bloquesOptions
                            }
                            name='bloqueElegido'
                            value={this.state.bloqueElegido}
                            onChange={this.manejarCambioCreacion}
                            width='3'
                            fluid
                        />
                        <Form.Select
                            label='Día'
                            options={this.diasOptions}
                            name='diaElegido'
                            value={this.state.diaElegido}
                            onChange={this.manejarCambioCreacion}
                            width='2'
                            fluid
                        />
                        <Form.Select
                            label='Bloque horario'
                            options={
                                this.state.planElegido === 'electivo'
                                    ? bloqueHorarioAOption(bloquesHorariosElectivo)
                                    : this.state.planElegido === 'intensivo-vespertino'
                                    ? bloqueHorarioAOption(bloquesHorariosIntensivoVespertino)
                                    : bloqueHorarioAOption(bloquesHorariosAnualEIntensivo)
                            }
                            name='bloqueHorarioElegido'
                            value={this.state.bloqueHorarioElegido}
                            onChange={this.manejarCambioCreacion}
                            width='3'
                            fluid
                        />
                        <Form.Select
                            label='Sala'
                            options={this.salasOptions}
                            name='salaElegida'
                            value={this.state.salaElegida}
                            onChange={this.manejarCambioCreacion}
                            width='1'
                            fluid
                        />
                        <Form.Select
                            label='Sugerencia'
                            search
                            options={this.props.cursos
                                .map(c => ({
                                    key: c.id,
                                    value: c.id,
                                    text:
                                        `${c.nombre} - ${
                                            diaATexto[c.dia]
                                        } ${nombreDeBloqueHorarioDesdeObjeto(c.bloqueHorario)}` +
                                        ` - sala: ${c.sala}`,
                                }))
                                .concat([{ key: -1, value: -1, text: 'Nueva sugerencia' }])}
                            name='sugerenciaElegida'
                            value={this.state.sugerenciaElegida}
                            onChange={this.manejarCambioCreacion}
                            width='5'
                            fluid
                        />
                    </Form.Group>

                    <Form.Group>
                        <Form.Button
                            primary
                            content='Crear curso'
                            disabled={!creacionEsValida}
                            onClick={() => {
                                if (this.state.sugerenciaElegida === -1) {
                                    this.setState({ modalNuevaSugerenciaAbierto: true });
                                } else {
                                    this.crearNuevoCurso().catch();
                                }
                            }}
                        />

                        <Form.Button
                            content='Cerrar'
                            type='button'
                            onClick={() => {
                                this.setState({ mostrandoCrearCurso: false });
                            }}
                        />
                    </Form.Group>

                    <ModalEnfocable
                        open={this.state.modalNuevaSugerenciaAbierto}
                        onClose={() => {
                            this.setState({ modalNuevaSugerenciaAbierto: false });
                        }}
                        closeIcon
                    >
                        <Modal.Header>Nueva sugerencia</Modal.Header>
                        <Modal.Content>
                            <Form>
                                <Form.Field>
                                    <label>Elija el color para la nueva sugerencia</label>
                                    <input
                                        type='color'
                                        ref={x => {
                                            if (x) this.colorPicker = x;
                                        }}
                                    />
                                </Form.Field>
                            </Form>
                        </Modal.Content>
                        <Modal.Actions>
                            <Button
                                primary
                                content='Crear curso y sugerencia'
                                onClick={() => {
                                    this.setState({ modalNuevaSugerenciaAbierto: false }, () => {
                                        this.crearNuevoCurso(this.colorPicker!.value).catch();
                                    });
                                }}
                            />
                        </Modal.Actions>
                    </ModalEnfocable>
                </Form>
            </Segment>
        );
    }

    private readonly manejarCambioCreacion = (_e: {}, { name, value }: DropdownProps) => {
        this.setState({ ...this.state, [name]: value });
    };

    private readonly crearNuevoCurso = async (colorElegido?: string) => {
        this.props.creandoNuevoCurso();
        const {
            bloqueElegido,
            planElegido,
            diaElegido,
            salaElegida,
            bloqueHorarioElegido,
            sugerenciaElegida,
        } = this.state;

        try {
            if (sugerenciaElegida === -1 && colorElegido) {
                await clienteApi.ejecutar('abrirCurso', {
                    bloque: bloqueElegido as Argumentos<'abrirCurso'>['bloque'],
                    plan: planElegido,
                    dia: diaElegido as Dias,
                    sala: salaElegida,
                    bloqueHorario: nombreDeBloqueHorarioAObjeto(bloqueHorarioElegido),
                    colorDeSugerencia: colorElegido,
                });
            } else {
                await clienteApi.ejecutar('abrirCurso', {
                    bloque: bloqueElegido as Argumentos<'abrirCurso'>['bloque'],
                    plan: planElegido,
                    dia: diaElegido as Dias,
                    sala: salaElegida,
                    bloqueHorario: nombreDeBloqueHorarioAObjeto(bloqueHorarioElegido),
                    sugerenciaDelCurso: sugerenciaElegida,
                });
                this.setState(prev => ({
                    ...stateInicial,
                    mostrandoCrearCurso: prev.mostrandoCrearCurso,
                }));
            }
        } catch (error) {
            this.setState({ modalNuevaSugerenciaAbierto: false });
            this.context.mostrarModalError(error);
        }

        await this.props.creacionTerminada();
    };
}

function nombreDeBloqueHorarioAObjeto(nombre: string) {
    const [horaInicio, horaTermino, tipo] = nombre.split('-');
    return {
        horaInicio,
        horaTermino,
        tipo: tipo === 'v' ? ('vespertino' as const) : ('normal' as const),
    };
}

function nombreDeBloqueHorarioDesdeObjeto(bloque: BloqueHorario) {
    const { horaInicio, horaTermino, tipo } = bloque;
    return `${horaInicio}-${horaTermino}${tipo === 'vespertino' ? '-v' : ''}`;
}

function bloqueHorarioAOption(
    bloques: ReadonlyArray<{ id: string; nombre: string; vespertino: boolean }>,
) {
    return bloques.map(bloque => ({
        key: bloque.id,
        value: bloque.id,
        text: bloque.nombre + (bloque.vespertino ? ' (vespertino)' : ''),
    }));
}

export default CrearNuevoCurso;



// WEBPACK FOOTER //
// ./src/modulos/Horarios/Administrar/CrearNuevoCurso.tsx